import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Ícone de contorno animado (Lottie) do sidebar.
 *
 * Os arquivos são gerados por `scripts/lottie/build-sidebar-icons.mjs` e ficam em
 * `public/assets/sidebar-icons/`. Em repouso o ícone está parado no quadro 0, que
 * é idêntico ao último: a animação toca no hover e na chegada ao item, e volta ao
 * repouso sem salto.
 *
 * Todo traço e preenchimento do Lottie aponta para o slot de cor `ink`. O ícone
 * herda a cor do texto (`currentColor`): lemos a cor calculada do elemento e a
 * aplicamos ao slot, de novo sempre que o estado visual do item muda (`colorKey`)
 * ou o tema troca de classe no <html>.
 *
 * Usamos o `DotLottie` (de `@lottiefiles/dotlottie-web`) direto, e não o
 * `DotLottieReact`: o componente do React manda carregar a animação duas vezes (no
 * construtor e num efeito) e a cor aplicada ao slot logo após a primeira carga se
 * perdia, deixando o ícone na cor do arquivo.
 */
export const ANIMATED_ICON_DIR = '/assets/sidebar-icons';
export const COLOR_SLOT = 'ink';

const COLOR_RE = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:\s*[,/]\s*([\d.]+)(%?))?\s*\)$/;

/** "rgb(12, 34, 56)", "rgba(12, 34, 56, .5)" ou "rgb(12 34 56 / 50%)" para [r, g, b, a] de 0 a 1. */
export function parseCssColor(value) {
  const m = COLOR_RE.exec((value || '').trim());
  if (!m) return null;
  const alpha = m[4] === undefined ? 1 : m[5] ? Number(m[4]) / 100 : Number(m[4]);
  return [Number(m[1]) / 255, Number(m[2]) / 255, Number(m[3]) / 255, alpha];
}

const sameColor = (a, b) => Boolean(a) && a.every((v, i) => Math.abs(v - b[i]) < 1e-6);

/**
 * Redesenha o quadro atual do zero.
 *
 * `setColorSlot` redesenha o quadro, mas por cima do que já estava no canvas, sem
 * limpar: cada troca de cor com o ícone parado soma mais uma camada nas bordas
 * suavizadas do traço, e o ícone engrossa (e engrossava a cada hover e saída do
 * mouse, ficando pesado depois). Trocar de quadro e voltar redesenha limpo; durante a
 * animação não é preciso, porque cada quadro novo já é desenhado limpo.
 */
function redrawFrame(dot) {
  if (dot.isPlaying) return;
  const frame = dot.currentFrame || 0;
  dot.setFrame(frame > 0 ? frame - 1 : 1);
  dot.setFrame(frame);
}

/** A mesma cor com o vermelho um pouco deslocado (1 em 255): diferente para o slot, igual para o olho. */
const nudge = ([r, g, b, a]) => [r > 0.5 ? r - 1 / 255 : r + 1 / 255, g, b, a];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// O JSON de cada ícone é buscado uma vez por nome, mesmo que apareça em vários lugares.
// `async` para que qualquer falha (inclusive um `fetch` que nem existe) chegue como rejeição.
const dataByName = new Map();
async function loadIconData(name) {
  if (!dataByName.has(name)) {
    const request = fetch(`${ANIMATED_ICON_DIR}/${name}.json`)
      .then((response) => {
        if (!response.ok) throw new Error(`ícone ${name}: HTTP ${response.status}`);
        return response.text();
      })
      .catch((error) => {
        dataByName.delete(name); // uma falha de rede não pode ficar guardada para sempre
        throw error;
      });
    dataByName.set(name, request);
  }
  return dataByName.get(name);
}

/** Mesma paleta do texto do item: cyan ativo, destaque no hover, cinza em repouso. */
export function iconToneClass({ active, hovered }) {
  if (active) return 'text-cyan-700 dark:text-cyan-300';
  return hovered ? 'text-foreground' : 'text-foreground-muted';
}

/**
 * Estado que um item de menu precisa para animar o ícone: hover, e um contador
 * que sobe a cada "toque" (hover e chegada ao item ativo; a montagem não conta,
 * senão toda página abriria com o ícone se mexendo).
 */
export function useAnimatedIconState(active = false) {
  const [hovered, setHovered] = useState(false);
  const [playSignal, setPlaySignal] = useState(0);
  const wasActive = useRef(active);

  useEffect(() => {
    if (active && !wasActive.current) setPlaySignal((n) => n + 1);
    wasActive.current = active;
  }, [active]);

  const replay = useCallback(() => setPlaySignal((n) => n + 1), []);
  const handlers = {
    onMouseEnter: () => {
      setHovered(true);
      replay();
    },
    onMouseLeave: () => setHovered(false),
  };

  return { hovered, playSignal, replay, handlers };
}

/**
 * @param {object} props
 * @param {string} props.name        nome do arquivo em `sidebar-icons/` (sem extensão)
 * @param {Function} [props.fallback] ícone Lucide mostrado até o Lottie carregar (e para sempre se falhar)
 * @param {string} [props.className]  tamanho e cor (`h-5 w-5 text-...`)
 * @param {string} [props.colorKey]   muda quando a cor calculada pode ter mudado
 * @param {number} [props.playSignal] cada incremento toca a animação uma vez
 * @param {boolean} [props.playOnMount] toca uma vez ao ficar pronto (ex.: ícone recém-trocado)
 */
export function AnimatedIcon({ name, fallback: Fallback, className, colorKey, playSignal = 0, playOnMount = false }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [dot, setDot] = useState(null);
  const [ready, setReady] = useState(false);
  const lastSignal = useRef(playSignal);
  const appliedColor = useRef(null);

  // O player (JS + wasm) só baixa quando o primeiro ícone aparece: o sidebar vive no
  // shell, e puxar o player no bundle inicial pesaria em toda página. Até lá, e se
  // algo falhar, o ícone Lucide `fallback` ocupa o lugar.
  useEffect(() => {
    let cancelled = false;
    let instance = null;
    Promise.all([import('@lottiefiles/dotlottie-web'), loadIconData(name)])
      .then(([{ DotLottie }, data]) => {
        if (cancelled || !canvasRef.current) return;
        instance = new DotLottie({
          canvas: canvasRef.current,
          data,
          autoplay: false,
          loop: false,
          renderConfig: { autoResize: true },
        });
        setDot(instance);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      instance?.destroy();
      setDot(null);
      setReady(false);
    };
  }, [name]);

  const syncColor = useCallback(() => {
    if (!dot || !wrapRef.current) return;
    const rgba = parseCssColor(window.getComputedStyle(wrapRef.current).color);
    if (!rgba) return;
    // No dotlottie-web 0.63, pôr no slot a MESMA cor que ele já tem desfaz a troca e
    // devolve a cor do arquivo. Várias leituras seguidas dão sempre a mesma cor (carga,
    // pronto, hover), então só escrevemos quando ela de fato mudou.
    if (sameColor(appliedColor.current, rgba)) return;
    // O slot também sobrevive entre instâncias da mesma animação: um item que remonta (o
    // menu recolhe e expande) cria um player novo cujo slot já guarda a cor de antes, e
    // pôr a mesma de novo o desfaz. Por isso a cor entra em duas escritas, a primeira
    // um pouco deslocada, para a segunda nunca ser igual ao que o slot guarda.
    dot.setColorSlot(COLOR_SLOT, nudge(rgba));
    if (dot.setColorSlot(COLOR_SLOT, rgba)) appliedColor.current = rgba;
    redrawFrame(dot);
  }, [dot]);

  const play = useCallback(() => {
    if (!dot || prefersReducedMotion() || dot.isPlaying) return;
    dot.setFrame(0);
    dot.play();
  }, [dot]);

  useEffect(() => {
    if (!dot) return undefined;
    const onLoad = () => {
      appliedColor.current = null; // uma carga nova volta aos slots do arquivo
      syncColor();
      dot.setFrame(0);
      setReady(true);
      if (playOnMount) play();
    };
    if (dot.isLoaded) onLoad();
    dot.addEventListener('load', onLoad);
    return () => dot.removeEventListener('load', onLoad);
    // playOnMount só vale para o primeiro carregamento deste ícone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dot]);

  useEffect(() => {
    if (ready) syncColor();
  }, [ready, colorKey, syncColor]);

  // O tema troca a classe do <html>. O ícone herda a cor de um item que ainda
  // pode estar em transição, então relemos de novo quando ela assentar.
  useEffect(() => {
    if (!ready) return undefined;
    let timer;
    const observer = new MutationObserver(() => {
      syncColor();
      clearTimeout(timer);
      timer = setTimeout(syncColor, 260);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [ready, syncColor]);

  useEffect(() => {
    if (playSignal === lastSignal.current) return;
    lastSignal.current = playSignal;
    if (ready) play();
  }, [playSignal, ready, play]);

  return (
    <span
      ref={wrapRef}
      aria-hidden="true"
      data-animated-icon={name}
      className={cn('relative inline-block shrink-0', className)}
    >
      {!ready && Fallback ? <Fallback className="absolute inset-0 h-full w-full" /> : null}
      {/* `block`: um canvas em linha tem clientWidth 0, e o player não consegue dimensioná-lo. */}
      <canvas ref={canvasRef} className={cn('absolute inset-0 block h-full w-full', !ready && 'opacity-0')} />
    </span>
  );
}

export default AnimatedIcon;
